import { useRef } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from '../../../hooks/useWebSocket';
import { vesselChangesQueryKeys } from '../../vessel-changes';
import {
  vesselNoticeHeading,
  type EmployeeNotification,
  type NoticeGroup,
  type NotificationInbox,
} from '../api/employeeNotificationsApi';
import { showDesktopAlert } from '../alerts/desktopAlerts';
import { NoticeToast } from '../components/NoticeToast';
import { vesselNoticePath } from '../model/vesselNotices';
import { employeeNotificationKeys } from '../queryKeys';

/**
 * One socket for both bells. The server only says "employees X, Y have something new"; when my id is
 * in it, both bells and the vessel alerts reload, and a notice that is new in that reload pops up in
 * its bell's style. A new vessel notice also shows a desktop pop-up when allowed. No sound, no banner
 * (owner, 2026-10-13: the bell is enough). Opening it marks it read (through `onOpen`) and opens its
 * link, or Vessel changes (the Westports schedule page for an upload reminder).
 */
export function useNoticeSocket(companyId: number | null | undefined, employeeId: number,
                                onOpen: (notice: EmployeeNotification) => void) {
  const queryClient = useQueryClient();
  const lastShown = useRef<Record<NoticeGroup, number>>({ vessel: 0, other: 0 });

  const inboxOf = (group: NoticeGroup) =>
    queryClient.getQueryData<NotificationInbox>(employeeNotificationKeys.mine(employeeId, group));

  const openVessel = (notice: EmployeeNotification) => {
    onOpen({ ...notice, linkPath: vesselNoticePath(notice) });
  };

  const showVessel = (notice: EmployeeNotification) => {
    showDesktopAlert(notice, () => openVessel(notice));
    toast(
      (t) => (
        <NoticeToast notice={notice} heading={vesselNoticeHeading(notice.kind)}
          onClick={() => { toast.dismiss(t.id); openVessel(notice); }} />
      ),
      { icon: '🚢', duration: 15000, style: { border: '2px solid #dc2626' } },
    );
  };

  const showOther = (notice: EmployeeNotification) => {
    toast(
      (t) => <NoticeToast notice={notice} onClick={() => { toast.dismiss(t.id); onOpen(notice); }} />,
      { icon: '🔔', duration: 8000 },
    );
  };

  /** Only a notice the reload brought (not the one on top before it), unread and not shown yet. */
  const popUp = (group: NoticeGroup, newestBefore: number | undefined) => {
    const newest = inboxOf(group)?.items?.[0];
    if (!newest || newest.read || newest.id === newestBefore || newest.id === lastShown.current[group]) return;
    lastShown.current[group] = newest.id;
    if (group === 'vessel') showVessel(newest);
    else showOther(newest);
  };

  useWebSocket({
    topic: `/topic/employee-notifications/${companyId ?? 0}`,
    enabled: !!companyId && employeeId > 0,
    onMessage: async (msg: { employeeIds?: number[] } | string) => {
      const ids = typeof msg === 'object' && msg && Array.isArray(msg.employeeIds) ? msg.employeeIds : [];
      if (!ids.includes(employeeId)) return;
      const before = { vessel: inboxOf('vessel')?.items?.[0]?.id, other: inboxOf('other')?.items?.[0]?.id };
      void queryClient.invalidateQueries({ queryKey: vesselChangesQueryKeys.alerts() });
      await queryClient.invalidateQueries({ queryKey: employeeNotificationKeys.all });
      popUp('vessel', before.vessel);
      popUp('other', before.other);
    },
  });
}
